import React from "react";
import { Button, Card, Space, Tag, Typography } from "antd";
import { useNavigate } from "react-router-dom";
import { useGlobal } from "../../contexts/GlobalContext";

const { Text, Paragraph } = Typography;

// PRD v6 四档：free / pro / pro_max / business，对应 vip_info.level 0/1/2/3。
// 兼容历史调用方传入的 "Pro" / "Pro Max" / "Ultimate" / "Business" 等写法。
export type PlanKey = "free" | "pro" | "pro_max" | "business";

const PLAN_ORDER: Record<PlanKey, number> = {
  free: 0,
  pro: 1,
  pro_max: 2,
  business: 3,
};

const PLAN_LABEL: Record<PlanKey, string> = {
  free: "Free",
  pro: "Pro",
  pro_max: "Pro Max",
  business: "Business",
};

// 归一化外部传入的档位字符串到 PlanKey。历史上 Analytics 用过 "Ultimate" 表示最高档热力图/洞察。
function normalizePlan(raw: string | undefined): PlanKey {
  const v = (raw || "").trim().toLowerCase().replace(/[\s-]+/g, "_");
  switch (v) {
    case "free":
      return "free";
    case "pro":
      return "pro";
    case "pro_max":
    case "promax":
      return "pro_max";
    case "business":
    case "ultimate": // 旧写法：等同最高商业档
      return "business";
    default:
      return "free";
  }
}

// 由 vip_info.level 推导当前用户档位。level 0/1/2/3 -> free/pro/pro_max/business。
export function planFromLevel(level: number | undefined, isVip?: boolean): PlanKey {
  if (!isVip && (level === undefined || level <= 0)) return "free";
  switch (level) {
    case 3:
      return "business";
    case 2:
      return "pro_max";
    case 1:
      return "pro";
    default:
      return "free";
  }
}

interface PlanGateProps {
  feature: string;
  requiredPlan: string;
  /** 显式覆盖当前档位（不传则从 GlobalContext 的 user 读取） */
  currentPlan?: string;
  description?: string;
  compact?: boolean;
  /** 达标时渲染的真实功能。不传则只做横幅展示（向后兼容旧调用） */
  children?: React.ReactNode;
}

// PlanGate 真权益门（PRD v6 R1-B）：
// - 当前档位 >= requiredPlan：渲染 children（真实功能），不显升级卡。
// - 档位不足：隐藏 children，仅显升级卡（跳转 /payment-checkout?type=member）。
// 注意：后端仍强校验，前端只做 UX 门控。
const PlanGate: React.FC<PlanGateProps> = ({
  feature,
  requiredPlan,
  currentPlan,
  description,
  compact = false,
  children,
}) => {
  const navigate = useNavigate();
  const { user } = useGlobal();

  const required = normalizePlan(requiredPlan);
  const vipInfo = user?.apiInfo?.postchat?.vip_info;
  const current = currentPlan
    ? normalizePlan(currentPlan)
    : planFromLevel(vipInfo?.level, vipInfo?.is_vip);

  const allowed = PLAN_ORDER[current] >= PLAN_ORDER[required];

  if (allowed) {
    // 达标：直接放行真实功能（无 children 时返回 null，不再显示恒显装饰条）。
    return <>{children ?? null}</>;
  }

  return (
    <Card size="small" style={{ borderStyle: "dashed", background: "#fafafa" }}>
      <Space direction={compact ? "horizontal" : "vertical"} size={compact ? 8 : 12} wrap>
        <Space size={8} wrap>
          <Tag color="blue">{PLAN_LABEL[required]}</Tag>
          <Text strong>{feature}</Text>
          <Text type="secondary">当前套餐: {PLAN_LABEL[current]}</Text>
        </Space>
        {!compact && (
          <Paragraph type="secondary" style={{ margin: 0 }}>
            {description || "升级套餐后可解锁该能力，后端仍会进行权益校验。"}
          </Paragraph>
        )}
        <Button type="primary" onClick={() => navigate("/payment-checkout?type=member")}>
          升级套餐
        </Button>
      </Space>
    </Card>
  );
};

export default PlanGate;
